import { useEffect, useState } from "react";
import CountUp from "react-countup";
import * as Tooltip from "@radix-ui/react-tooltip";
import { compactNumberFormatter, numberOrganizer } from "src/util/Util";

type NumberRollProps = {
  number: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  decimals?: number;
  compact?: boolean;
};

const NumberRoll = ({
  number,
  suffix,
  prefix,
  className,
  decimals,
  compact = false,
}: NumberRollProps) => {
  const [oldCount, setOldCount] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setOldCount(count);
    setCount(number);
  }, [number]);

  const formatFn = (value: number) => {
    if (Math.abs(value) >= 10000) {
      const formatted = compactNumberFormatter.format(value);
      return `${prefix || ""}${formatted}${suffix || ""}`;
    }
    const formatted = numberOrganizer.format(Number(value.toFixed(decimals ?? 2)));
    return `${prefix || ""}${formatted}${suffix || ""}`;
  };

  const fullValue = `${prefix || ""}${numberOrganizer.format(Number(Math.abs(number).toFixed(decimals ?? 0)))}${suffix || ""}`;

  const countUpElement = (
    <CountUp
      className={className}
      start={oldCount}
      end={count}
      duration={1}
      useEasing={true}
      {...(compact
        ? { formattingFn: formatFn, decimals: decimals ?? 2 }
        : { separator: ",", prefix, suffix, decimals }
      )}
    />
  );

  const showTooltip = compact && Math.abs(number) >= 10000;

  if (!showTooltip) {
    return countUpElement;
  }

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="cursor-pointer">{countUpElement}</span>
        </Tooltip.Trigger>
        <Tooltip.Content
          side="top"
          align="center"
          sideOffset={5}
          className="rounded-md bg-black text-white px-3 py-2 text-sm shadow-md z-[999]"
        >
          {fullValue}
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default NumberRoll;
